import { Cpu, CreditCard, Gauge, MessageSquare, Users } from "lucide-react";
import { t } from "../../i18n";
import { NumberField } from "../NumberField";

export type OrganizationPricingValue = {
  configured: boolean;
  currency: string;
  version: number;
  effectiveFrom: string | null;
  prices: Record<string, number>;
};

const money = (micros: number) => micros / 1_000_000;
const micros = (amount: number) =>
  Math.max(0, Math.round((Number.isFinite(amount) ? amount : 0) * 1_000_000));

export function OrganizationPricingEditor({
  value,
  saving,
  onChange,
  onSave,
}: {
  value: OrganizationPricingValue;
  saving: boolean;
  onChange: (value: OrganizationPricingValue) => void;
  onSave: () => void;
}) {
  const set = (key: string, next: number) =>
    onChange({
      ...value,
      prices: { ...value.prices, [key]: next },
    });
  const currency = value.currency;
  return (
    <section className="space-y-5">
      <div className="panel overflow-hidden">
        <div className="bg-gradient-to-br from-lumen-600 to-lumen-800 p-6 text-white">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-widest text-white/70 uppercase">
                {t("Organization pricing")}
              </p>
              <h2 className="mt-1 text-2xl font-bold">
                {t("Simple, direct pricing")}
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-white/80">
                {t(
                  "Set what this organization pays. Billing versions and subscription linkage are handled automatically.",
                )}
              </p>
            </div>
            <label className="min-w-28">
              <span className="block text-xs text-white/70">
                {t("Currency")}
              </span>
              <select
                className="mt-1 w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white"
                value={value.currency}
                onChange={(event) =>
                  onChange({ ...value, currency: event.target.value })
                }
              >
                <option className="text-stone-900" value="EUR">
                  {t("EUR")}
                </option>
                <option className="text-stone-900" value="USD">
                  {t("USD")}
                </option>
                <option className="text-stone-900" value="GBP">
                  {t("GBP")}
                </option>
              </select>
            </label>
          </div>
        </div>
        <div className="grid gap-4 p-5 md:grid-cols-3">
          <NumberField
            label={t("Monthly organization fee")}
            value={money(value.prices.baseFeeMicros ?? 0)}
            suffix={currency}
            step="0.01"
            onChange={(next) => set("baseFeeMicros", micros(next))}
          />
          <NumberField
            label={t("Minimum monthly charge")}
            value={money(value.prices.minimumFeeMicros ?? 0)}
            suffix={currency}
            step="0.01"
            onChange={(next) => set("minimumFeeMicros", micros(next))}
          />
          <NumberField
            label={t("Tax rate")}
            value={(value.prices.taxBps ?? 0) / 100}
            suffix="%"
            step="0.01"
            onChange={(next) => set("taxBps", Math.round(next * 100))}
          />
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="panel p-5">
          <div className="mb-4 flex items-center gap-3">
            <span className="rounded-xl bg-blue-100 p-2 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              <Users size={18} />
            </span>
            <div>
              <h3 className="font-bold">{t("Users")}</h3>
              <p className="text-xs text-stone-500">
                {t("Included seats and the price for each additional user.")}
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              label={t("Included users")}
              value={value.prices.includedSeats ?? 0}
              onChange={(next) => set("includedSeats", Math.round(next))}
            />
            <NumberField
              label={t("Price per additional user")}
              value={money(value.prices.seatFeeMicros ?? 0)}
              suffix={currency}
              step="0.01"
              onChange={(next) => set("seatFeeMicros", micros(next))}
            />
          </div>
        </div>

        <div className="panel p-5">
          <div className="mb-4 flex items-center gap-3">
            <span className="rounded-xl bg-violet-100 p-2 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
              <MessageSquare size={18} />
            </span>
            <div>
              <h3 className="font-bold">{t("Prompts")}</h3>
              <p className="text-xs text-stone-500">
                {t("Included prompts and the price for additional prompts.")}
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              label={t("Included prompts")}
              value={value.prices.includedPrompts ?? 0}
              onChange={(next) => set("includedPrompts", Math.round(next))}
            />
            <NumberField
              label={t("Price per additional prompt")}
              value={money(value.prices.promptFeeMicros ?? 0)}
              suffix={currency}
              step="0.000001"
              onChange={(next) => set("promptFeeMicros", micros(next))}
            />
          </div>
        </div>

        <div className="panel p-5">
          <div className="mb-4 flex items-center gap-3">
            <span className="rounded-xl bg-amber-100 p-2 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              <Cpu size={18} />
            </span>
            <div>
              <h3 className="font-bold">{t("Compute and storage")}</h3>
              <p className="text-xs text-stone-500">
                {t("Optional metered prices for workspace resources.")}
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              label={t("Workspace CPU second")}
              value={money(value.prices.workspaceCpuSecondMicros ?? 0)}
              suffix={currency}
              step="0.000001"
              onChange={(next) => set("workspaceCpuSecondMicros", micros(next))}
            />
            <NumberField
              label={t("Storage GB-day")}
              value={money(value.prices.storageGbDayMicros ?? 0)}
              suffix={currency}
              step="0.000001"
              onChange={(next) => set("storageGbDayMicros", micros(next))}
            />
            <NumberField
              label={t("Workspace memory MB-second")}
              value={money(value.prices.workspaceMemoryMbSecondMicros ?? 0)}
              suffix={currency}
              step="0.000001"
              onChange={(next) =>
                set("workspaceMemoryMbSecondMicros", micros(next))
              }
            />
            <NumberField
              label={t("Model-cost markup")}
              value={(value.prices.modelCostMarkupBps ?? 0) / 100}
              suffix="%"
              step="0.01"
              onChange={(next) =>
                set("modelCostMarkupBps", Math.round(next * 100))
              }
            />
          </div>
        </div>

        <div className="panel p-5">
          <div className="mb-4 flex items-center gap-3">
            <span className="rounded-xl bg-emerald-100 p-2 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              <Gauge size={18} />
            </span>
            <div>
              <h3 className="font-bold">{t("Actions")}</h3>
              <p className="text-xs text-stone-500">
                {t("Optional prices for billable actions.")}
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["automationRunMicros", "Automation run"],
              ["toolExecutionMicros", "Tool execution"],
              ["imageGenerationMicros", "Image generation"],
            ].map(([key]) => (
              <NumberField
                key={key}
                label={
                  key === "automationRunMicros"
                    ? t("Automation run")
                    : key === "toolExecutionMicros"
                      ? t("Tool execution")
                      : t("Image generation")
                }
                value={money(value.prices[key] ?? 0)}
                suffix={currency}
                step="0.000001"
                onChange={(next) => set(key, micros(next))}
              />
            ))}
            <NumberField
              label={t("Discount")}
              value={(value.prices.discountBps ?? 0) / 100}
              suffix="%"
              step="0.01"
              onChange={(next) => set("discountBps", Math.round(next * 100))}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-4 flex items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white/95 p-4 shadow-xl backdrop-blur dark:border-white/10 dark:bg-stone-900/95">
        <span className="flex items-center gap-2 text-sm text-stone-500">
          <CreditCard size={17} />
          {value.configured
            ? t("Saving creates a new auditable pricing version.")
            : t("Pricing will be activated when you save.")}
        </span>
        <button className="button-primary" disabled={saving} onClick={onSave}>
          {saving ? t("Saving…") : t("Save organization pricing")}
        </button>
      </div>
    </section>
  );
}
